/*  MUI  */
import { Box, Skeleton, Typography } from '@mui/material';

/* Shared */
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/*  Components  */
import ApplicationFunnelReports from '../../ApplicationFunnelReports';
import { QBRManagedTable } from './QBRManagedTable';
import { QBRReportHeader } from './QBRReportHeader';
import { QBRScoreHistoryAndInsights } from './QBRScoreHistoryAndInsights';

const getConfig = async ({ clientId }) => {
    const params = {};
    if (clientId) {
        params.platformTenantId = clientId;
        params.includeClients = true;
    }

    const { CancelToken } = axios;
    const source = CancelToken.source();

    try {
        const response = await axios.get('/reports/config', {
            cancelToken: source.token,
            params
        });
        if (response?.data?.data) {
            return response.data.data;
        }
        debug.e('QBR Error. No data in Report Config:', response);
        return false;
    } catch (e) {
        debug.e('QBR Error getting Report Config:', e);
        return false;
    }
};

/*  return [err, reportResponse];  */
const runReportFunct = async ({ clientId, endpoint, p }) => {
    const params = p;
    if (clientId) {
        params.platformTenantId = clientId;
    }
    const { CancelToken } = axios;
    const source = CancelToken.source();

    try {
        const response = await axios.get(endpoint, {
            cancelToken: source.token,
            params
        });
        if (response?.data) {
            const { data } = response.data;
            if (data) {
                return [false, data];
            }
        }
    } catch (e) {
        debug.e('Error running Report:', e);
    }

    return [true, { reportData: {} }];
};

const getAppFunnelReportProps = async ({
    applicationOrCategorySelection,
    clientId,
    endTime,
    height,
    noLegend,
    period,
    reportCheckbox,
    reportSelection,
    setAppFunnelReportProps,
    setLoaded,
    setReportRunError,
    startTime,
    type,
    userOrGroupSelection
}) => {
    const reportPayload = {
        applicationOrCategorySelection,
        applicationSelection: '[0]',
        endTime,
        groupSelection: '[0]',
        period,
        platformSelection: '[0]',
        reportSelections: {
            [type + reportSelection + reportCheckbox]: true
        },
        startTime,
        statusSelection: '[0]',
        tagSelection: '[0]',
        type,
        userOrGroupSelection,
        userSelection: '[0]'
    };

    let report;
    let subReport;

    const config = await getConfig({ clientId });
    config?.forEach((item) => {
        if (item.type === reportPayload.type) {
            report = item;
            const { checkboxes } = item;

            Object.values(checkboxes).forEach((checkbox) => {
                const { subOptions } = checkbox;
                Object.values(subOptions).forEach((subOption) => {
                    if (
                        checkbox.type + subOption.key ===
                        reportSelection + reportCheckbox
                    ) {
                        subReport = checkbox;
                    }
                });
            });
        }
    });

    if (report === undefined || subReport === undefined) {
        setLoaded(true);
        return;
    }

    const endpoint = `/reports/${subReport.reportEndpoint}`;

    const [reportRunError, reportResponse] = await runReportFunct({
        clientId,
        endpoint,
        p: reportPayload
    });

    setReportRunError(reportRunError);

    const { reportData } = reportResponse;

    const props = {
        height,
        renderingScheduledReport: false,
        reportData,
        reportSetup: reportPayload,
        reportWrapper: true,
        reportWrapperProps: {
            applicationOrCategorySelection,
            endTime,
            height,
            noLegend,
            period,
            reportCheckbox,
            reportSelection,
            startTime,
            type,
            userOrGroupSelection
        }
    };
    setAppFunnelReportProps(props);
    setLoaded(true);
};

export const QBRFirstApplicationClassificationResults = (props) => {
    const { t } = useTranslation();
    const { clientId } = useParams();
    const { reportData, reportSetup, reportWrapper } = props;
    const [loaded, setLoaded] = useState(false);
    const [reportRunError, setReportRunError] = useState(null);
    const [appFunnelReportProps, setAppFunnelReportProps] = useState({});

    const reportType = 'AppClassificationScore';
    const startTime = new Date(reportSetup.startTime).toISOString();
    const endTime = new Date(reportSetup.endTime).toISOString();
    const { period } = reportSetup;

    useEffect(() => {
        getAppFunnelReportProps({
            applicationOrCategorySelection: 'application',
            clientId,
            endTime,
            height: 200,
            noLegend: true,
            period,
            reportCheckbox: 'Breakdown',
            reportSelection: 'All',
            setAppFunnelReportProps,
            setLoaded,
            setReportRunError,
            startTime,
            type: 'applicationFunnel',
            userOrGroupSelection: 'all'
        });
    }, [clientId, endTime, period, startTime]);

    if (reportRunError) {
        debug.e('Report Run Error:', reportRunError);
        return null;
    }

    return (
        <>
            <QBRReportHeader {...{ ...props, reportType }} />
            {!reportWrapper && (
                <QBRScoreHistoryAndInsights
                    reportType={reportType}
                    props={{ ...props }}
                />
            )}
            <Box
                style={{
                    height: '275px',
                    marginBottom: '16px',
                    marginLeft: '30px',
                    marginRight: '30px'
                }}
            >
                <Typography variant="h5" style={{ marginBottom: '8px' }}>
                    {t('QBR.applicationClassificationStatus')}
                </Typography>
                {loaded ? (
                    <ApplicationFunnelReports {...appFunnelReportProps} />
                ) : (
                    <div
                        style={{
                            display: 'block',
                            height: 250,
                            textAlign: 'center'
                        }}
                    >
                        <Skeleton height={250} />
                    </div>
                )}
            </Box>
            <hr />
            <QBRManagedTable
                rows={reportData.topManaged}
                rowsPerPage={10}
                title={t('QBR.top10ManagedApplicationsByUsage')}
            />
        </>
    );
};
